// ─── Modelo de permissões e roles ───────────────────────────────────────────────
// Fonte da verdade: "Mapa de permissões" do projeto no Linear
// (Criar permissão para usuários dos payfacs & matar BARTE_ADMIN).
// Contexto: portal PayFac — gestão de acessos do payfac "PagFácil".

import {
  ArrowsLeftRight,
  ChartBar,
  CreditCard,
  DeviceMobile,
  Eye,
  Headset,
  Plug,
  ShieldCheck,
  UserPlus,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';

// Ícone por perfil (usado no drawer e nas pills da tabela)
export const ROLE_ICON: Record<string, PhosphorIcon> = {
  base: Eye,
  relatorios: ChartBar,
  integracao: Plug,
  repasse: ArrowsLeftRight,
  'financeiro-taxas': CreditCard,
  credenciamento: UserPlus,
  'pos-tef': DeviceMobile,
  suporte: Headset,
  admin: ShieldCheck,
};

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

// ─── Usuário base — cumulativo, aplicado a todos ────────────────────────────────

export const BASE_ROLE: Role = {
  id: 'base',
  name: 'Visualização',
  description: 'Saldo, transações, cadastro e lista de sellers.',
  permissions: [
    'Visualização do saldo dos sellers',
    'Visualização dos dados cadastrais dos sellers',
    'Visualização da lista de sellers',
    'Visualização das transações',
  ],
};

// ─── Perfis atribuíveis (Mapa de permissões) ────────────────────────────────────

export const ROLES: Role[] = [
  {
    id: 'relatorios',
    name: 'Análises',
    description: 'Acessa relatórios do PSP e indicadores estratégicos.',
    permissions: ['Relatórios do PSP', 'Indicadores estratégicos'],
  },
  {
    id: 'integracao',
    name: 'Integração',
    description: 'Administra chaves de API e webhooks da integração.',
    permissions: ['Gestão de chaves API', 'Gestão dos webhooks'],
  },
  {
    id: 'repasse',
    name: 'Repasses',
    description: 'Edita dados bancários de repasse e solicita saques.',
    permissions: ['Edição de dados de repasse (dados bancários)', 'Solicitação de saque'],
  },
  {
    id: 'financeiro-taxas',
    name: 'Financeiro',
    description: 'Cria e edita planos de taxa e vincula aos sellers.',
    permissions: [
      'Criação de plano de taxa',
      'Edição de taxas',
      'Vinculação de plano de taxas ao seller',
      'Visualização da tela de pendências',
      'Configuração de parceiros e negociações',
    ],
  },
  {
    id: 'credenciamento',
    name: 'Credenciamento',
    description: 'Cadastra sellers, planos e usuários dos sellers.',
    permissions: [
      'Criação de seller',
      'Criação de plano de taxa',
      'Criação dos usuários dos sellers',
      'Edição de dados cadastrais (fora dados bancários)',
    ],
  },
  {
    id: 'pos-tef',
    name: 'POS/TEF',
    description: 'Vincula e gerencia maquininhas dos sellers.',
    permissions: ['Vinculação de maquininha'],
  },
  {
    id: 'suporte',
    name: 'Suporte',
    description: 'Ações de suporte: estornos, comprovantes e disputas.',
    permissions: [
      'Exportar relatórios dos sellers',
      'Estorno de transações',
      'Baixar comprovante de pagamento',
      'Envio de disputas dos chargebacks',
    ],
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Controle total de gestão, criação, edição e visualização.',
    permissions: ['Gestão dos usuários e permissões'],
  },
];

// Admin = acesso total: agrega as permissões de todos os perfis + base
{
  const adminRole = ROLES.find((r) => r.id === 'admin');
  if (adminRole) {
    const all = new Set<string>([...BASE_ROLE.permissions, ...adminRole.permissions]);
    ROLES.forEach((r) => {
      if (r.id !== 'admin') r.permissions.forEach((p) => all.add(p));
    });
    adminRole.permissions = Array.from(all);
  }
}

// Empresas (companies) do payfac disponíveis para dar acesso
export const COMPANIES: string[] = [
  'PagFácil Adquirência',
  'PagFácil Recebíveis',
  'PagFácil Pagamentos',
  'PagFácil Banking',
  'PagFácil Tecnologia',
  'PagFácil Marketplace',
];

export const ROLE_BY_ID: Record<string, Role> = [BASE_ROLE, ...ROLES].reduce(
  (acc, r) => {
    acc[r.id] = r;
    return acc;
  },
  {} as Record<string, Role>,
);

// ─── Usuários (mock) ────────────────────────────────────────────────────────────

export type UserStatus = 'Ativo' | 'Pendente' | 'Expirado' | 'Inativo';

export interface User {
  id: string;
  nome: string;
  email: string;
  status: UserStatus;
  roleIds: string[];
  lastAccess: string;
  companies: string[];
}

export const MOCK_USERS: User[] = [
  { id: 'USR-001', nome: 'Ricardo Almeida',  email: 'ricardo.almeida@pagfacil.com.br',  status: 'Ativo',    roleIds: ['admin'],                        lastAccess: '16/07/2026 09:42', companies: ['PagFácil Adquirência', 'PagFácil Recebíveis'] },
  { id: 'USR-002', nome: 'Juliana Santos',   email: 'juliana.santos@pagfacil.com.br',   status: 'Ativo',    roleIds: ['financeiro-taxas'],             lastAccess: '17/07/2026 08:15', companies: ['PagFácil Adquirência'] },
  { id: 'USR-003', nome: 'Marcos Oliveira',  email: 'marcos.oliveira@pagfacil.com.br',  status: 'Ativo',    roleIds: ['pos-tef', 'suporte'],           lastAccess: '16/07/2026 18:03', companies: ['PagFácil Adquirência'] },
  { id: 'USR-004', nome: 'Camila Ferreira',  email: 'camila.ferreira@pagfacil.com.br',  status: 'Pendente', roleIds: ['suporte'],                      lastAccess: '—',                companies: ['PagFácil Adquirência'] },
  { id: 'USR-005', nome: 'Bruno Costa',      email: 'bruno.costa@pagfacil.com.br',      status: 'Ativo',    roleIds: ['credenciamento'],               lastAccess: '15/07/2026 11:27', companies: ['PagFácil Adquirência'] },
  { id: 'USR-006', nome: 'Patrícia Lima',    email: 'patricia.lima@pagfacil.com.br',    status: 'Ativo',    roleIds: ['repasse'],                      lastAccess: '17/07/2026 07:50', companies: ['PagFácil Adquirência', 'PagFácil Recebíveis'] },
  { id: 'USR-007', nome: 'Fernando Souza',   email: 'fernando.souza@pagfacil.com.br',   status: 'Inativo',  roleIds: ['suporte'],                      lastAccess: '02/05/2026 14:11', companies: ['PagFácil Adquirência'] },
  { id: 'USR-008', nome: 'Aline Rodrigues',  email: 'aline.rodrigues@pagfacil.com.br',  status: 'Ativo',    roleIds: ['relatorios', 'financeiro-taxas'], lastAccess: '16/07/2026 16:39', companies: ['PagFácil Adquirência'] },
  { id: 'USR-009', nome: 'Gustavo Pereira',  email: 'gustavo.pereira@pagfacil.com.br',  status: 'Expirado', roleIds: ['credenciamento'],               lastAccess: '—',                companies: ['PagFácil Adquirência'] },
  { id: 'USR-010', nome: 'Larissa Martins',  email: 'larissa.martins@pagfacil.com.br',  status: 'Ativo',    roleIds: ['suporte'],                      lastAccess: '17/07/2026 09:10', companies: ['PagFácil Adquirência'] },
  { id: 'USR-011', nome: 'Rafael Carvalho',  email: 'rafael.carvalho@pagfacil.com.br',  status: 'Ativo',    roleIds: ['credenciamento', 'financeiro-taxas'], lastAccess: '14/07/2026 10:55', companies: ['PagFácil Adquirência'] },
  { id: 'USR-012', nome: 'Beatriz Gomes',    email: 'beatriz.gomes@pagfacil.com.br',    status: 'Pendente', roleIds: ['relatorios'],                   lastAccess: '—',                companies: ['PagFácil Adquirência'] },
  { id: 'USR-013', nome: 'Thiago Ribeiro',   email: 'thiago.ribeiro@pagfacil.com.br',   status: 'Ativo',    roleIds: ['pos-tef'],                      lastAccess: '16/07/2026 13:22', companies: ['PagFácil Adquirência'] },
  { id: 'USR-014', nome: 'Mariana Alves',    email: 'mariana.alves@pagfacil.com.br',    status: 'Inativo',  roleIds: ['suporte'],                      lastAccess: '20/03/2026 08:47', companies: ['PagFácil Adquirência'] },
  { id: 'USR-015', nome: 'Eduardo Nunes',    email: 'eduardo.nunes@pagfacil.com.br',    status: 'Ativo',    roleIds: ['integracao'],                   lastAccess: '15/07/2026 17:34', companies: ['PagFácil Adquirência'] },
  { id: 'USR-016', nome: 'Vanessa Rocha',    email: 'vanessa.rocha@pagfacil.com.br',    status: 'Ativo',    roleIds: ['repasse', 'relatorios'],        lastAccess: '17/07/2026 08:58', companies: ['PagFácil Adquirência', 'PagFácil Recebíveis'] },
  { id: 'USR-017', nome: 'Felipe Barbosa',   email: 'felipe.barbosa@pagfacil.com.br',   status: 'Expirado', roleIds: ['credenciamento'],               lastAccess: '—',                companies: ['PagFácil Adquirência'] },
  { id: 'USR-018', nome: 'Carolina Dias',    email: 'carolina.dias@pagfacil.com.br',    status: 'Ativo',    roleIds: ['admin'],                        lastAccess: '16/07/2026 19:05', companies: ['PagFácil Adquirência', 'PagFácil Recebíveis'] },
];
